import { useCallback, useMemo } from 'react'
import { Vector3 } from 'three'
import { useSceneElement } from '@editorHooks'
import SupportSceneCheckbox from './SupportSceneCheckbox'

interface Props {
  elementGuid: string
  supportIndex: number
  position: Vector3
  isChecked: boolean
  onToggle: (elementGuid: string, supportIndex: number) => void
}

/**
 * Registers one support's checkbox with the scene. Its properties are memoized
 * on the plain position and checked state — a new object would take the checkbox
 * out of the scene and put it back, which the live recompute would do on every
 * keystroke.
 */
const SupportSceneCheckboxEffect = ({
  elementGuid,
  supportIndex,
  position,
  isChecked,
  onToggle,
}: Props): null => {
  const { x, y, z } = position

  const handleToggle = useCallback(
    () => onToggle(elementGuid, supportIndex),
    [onToggle, elementGuid, supportIndex],
  )

  const properties = useMemo(
    () => ({
      x,
      y,
      z,
      isChecked,
      onToggle: handleToggle,
      dataCy: `support-compression-scene-checkbox-${elementGuid}-${supportIndex}`,
    }),
    [x, y, z, isChecked, handleToggle, elementGuid, supportIndex],
  )

  useSceneElement({ enabled: true, component: SupportSceneCheckbox, properties })

  return null
}

export default SupportSceneCheckboxEffect
